import config from '@/config';

/**
 * Builds standard authenticated headers for API requests
 */
export function buildAuthHeaders(token: string, csrfToken: string | null): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
    'X-CSRF-Token': csrfToken || ''
  };

  // Add development headers
  if (import.meta.env.VITE_APP_ENV === 'development') {
    headers['ngrok-skip-browser-warning'] = 'true';
  }

  return headers;
}

/**
 * Makes an authenticated API request with explicit token parameters
 */
export async function authenticatedFetch(
  endpoint: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  token: string,
  csrfToken: string | null,
  body?: unknown
): Promise<Response> {
  const headers = buildAuthHeaders(token, csrfToken);

  const options: RequestInit = {
    method,
    credentials: 'include',
    headers
  };

  if (body !== undefined) {
    options.body = JSON.stringify(body);
  }

  const response = await fetch(`${config.baseUrl}/${endpoint}`, options);

  if (!response.ok) {
    throw new Error(`API request failed: ${response.statusText}`);
  }

  return response;
}

interface FetchOptions extends RequestInit {
  headers?: Record<string, string>;
}

/**
 * Simple API fetch with automatic CSRF token handling from localStorage
 * Use this for simpler authenticated requests where you don't need explicit token control
 */
export async function apiFetch(url: string, options: FetchOptions = {}): Promise<Response> {
  const { headers = {}, ...restOptions } = options;

  // Get CSRF token from localStorage (set during login/refresh)
  const csrfToken = localStorage.getItem('csrf_token');

  const enhancedHeaders: Record<string, string> = {
    ...headers,
  };

  // Add CSRF token header if available and this is a state-changing request
  if (csrfToken && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(options.method?.toUpperCase() || '')) {
    enhancedHeaders['X-CSRF-Token'] = csrfToken;
  }

  return fetch(url, {
    ...restOptions,
    credentials: 'include', // Always include cookies for cross-origin requests
    headers: enhancedHeaders,
  });
}
